import React, {useState} from 'react';
import styles from './TextCard.module.css';
import Calendar from '../calendar/Calendar';

interface BaseTextCardItem {
    label: string;
    value: string;
    disabled?: boolean;
}

interface TextInputItem extends BaseTextCardItem {
    type: 'text';
    onChange?: (value: string) => void;
    options?: never;
}

interface SelectInputItem extends BaseTextCardItem {
    type: 'select';
    options: string[];
    onChange?: (value: string) => void;
}

interface DateInputItem extends BaseTextCardItem {
    type: 'date';
    onChange?: (value: string) => void;
    options?: never;
}

interface StaticTextItem extends BaseTextCardItem {
    type?: 'static';
    onChange?: never;
    options?: never;
}

type TextCardItem = TextInputItem | SelectInputItem | DateInputItem | StaticTextItem;

interface TextCardProps {
    items: TextCardItem[];
    className?: string;
}

//카드 형식 컴포넌트, 좌우 한쌍으로 출력
const TextCard: React.FC<TextCardProps> = ({items, className}) => {
    const [showCalendar, setShowCalendar] = useState<string | null>(null);

    //날짜 데이터 요일 표시
    const formatDate = (dateStr: string) => {
        try {
            const date = new Date(dateStr + 'T00:00:00');
            if (isNaN(date.getTime())) return '';
            const day = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
            return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}(${day})`;
        } catch {
            return '';
        }
    };

    //날짜 데이터 형식 변환, YYYY-MM-DD 형식
    const handleDateSelect = (date: Date, onChange?: (value: string) => void) => {
        if (onChange) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;
            onChange(formattedDate);
        }
        setShowCalendar(null);
    };

    const renderValue = (item: TextCardItem) => {
        switch (item.type) {
            //text인 경우 문자 표기
            case 'text':
                return (
                    <input
                        type="text"
                        value={item.value}
                        onChange={(e) => item.onChange?.(e.target.value)}
                        className={styles.input}
                        disabled={item.disabled}
                    />
                );
            //select인 경우 드롭박스 통해 아이템 선택
            case 'select':
                return (
                    <select
                        value={item.value}
                        onChange={(e) => item.onChange?.(e.target.value)}
                        className={styles.select}
                        disabled={item.disabled}
                    >
                        {item.options.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );
            //date인 경우 날짜 선택
            case 'date':
                return (
                    <div className={styles.dateContainer}>
                        <input
                            //날짜 표기
                            type="text"
                            value={item.value ? formatDate(item.value) : ''}
                            readOnly
                            className={styles.dateInput}
                            onClick={() => setShowCalendar(showCalendar === item.label ? null : item.label)}
                        />
                        {showCalendar === item.label && (
                            <div className={styles.calendarPopup}>
                                {/*Calendar 컴포넌트 사용*/}
                                <Calendar
                                    selectedDate={item.value ? new Date(item.value) : new Date()}
                                    onDateSelect={(date) => handleDateSelect(date, item.onChange)}
                                />
                            </div>
                        )}
                    </div>
                );
            default:
                return <span className={styles.value}>{item.value}</span>;
        }
    };

    return (
        <div className={`${styles.container} ${className || ''}`}>
            {items.map((item, index) => (
                <div key={index} className={styles.row}>
                    <span className={styles.label}>{item.label}</span>
                    {renderValue(item)}
                </div>
            ))}
        </div>
    );
};

export default TextCard;